import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Search, SlidersHorizontal } from "lucide-react";

export default function ShopPage() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const sort = params.get("sort") || "featured";

    const setParam = (k, v) => {
        const next = new URLSearchParams(params);
        if (v) next.set(k, v); else next.delete(k);
        setParams(next);
    };

    useEffect(() => {
        api.get("/products/categories").then((r) => setCategories(r.data));
    }, []);

    useEffect(() => {
        setLoading(true);
        api.get("/products", { params: { q, category, sort } }).then((r) => {
            // Collapse variant groups so each product family shows ONE tile in
            // the catalog. Non-canonical variants (e.g. glp-3r-30mg) are still
            // reachable via direct URL + the strength selector on the page.
            const data = r.data;
            setItems(data);
            setLoading(false);
        });
    }, [q, category, sort]);

    return (
        <div data-testid="shop-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="mb-12">
                    <div className="data-label mb-3 text-brand">CATALOG</div>
                    <h1 className="font-serif text-5xl md:text-6xl leading-none">Research Compounds</h1>
                    <p className="text-secondary mt-4 max-w-2xl">
                        Every Aminoplex compound is third-party verified to ≥99% purity. Browse, filter, and verify with batch-specific COAs.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Sidebar filters */}
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-32 space-y-7">
                            <div>
                                <div className="font-display text-base font-bold mb-3 flex items-center gap-2 text-[var(--text-primary)]"><Search size={16} /> Search</div>
                                <input
                                    data-testid="shop-search-input"
                                    value={q}
                                    onChange={(e) => setParam("q", e.target.value)}
                                    placeholder="BPC-157, NAD+…"
                                    className="input"
                                />
                            </div>
                            <div>
                                <div className="font-display text-base font-bold mb-3 flex items-center gap-2 text-[var(--text-primary)]"><SlidersHorizontal size={16} /> Category</div>
                                <div className="space-y-1">
                                    <button data-testid="cat-all" onClick={() => setParam("category", "")} className={`block w-full text-left font-display font-bold text-base py-2 transition-colors ${!category ? "text-[var(--brand)]" : "text-[var(--text-primary)] hover:text-[var(--brand)]"}`}>
                                        All Compounds
                                    </button>
                                    {categories.map((c) => (
                                        <button
                                            key={c}
                                            data-testid={`cat-${c}`}
                                            onClick={() => setParam("category", c)}
                                            className={`block w-full text-left font-display font-bold text-base py-2 transition-colors ${category === c ? "text-[var(--brand)]" : "text-[var(--text-primary)] hover:text-[var(--brand)]"}`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="font-display text-base font-bold mb-3 text-[var(--text-primary)]">Sort</div>
                                <select
                                    data-testid="sort-select"
                                    value={sort}
                                    onChange={(e) => setParam("sort", e.target.value)}
                                    className="input"
                                >
                                    <option value="featured">Featured</option>
                                    <option value="newest">Newest</option>
                                    <option value="price_asc">Price · Low to High</option>
                                    <option value="price_desc">Price · High to Low</option>
                                    <option value="name">Name · A-Z</option>
                                </select>
                            </div>
                            <div className="card p-5">
                                <div className="font-display font-bold text-sm mb-2 text-[var(--brand)]">Quantity Discount</div>
                                <p className="font-ui text-sm text-secondary leading-relaxed">
                                    2 vials → <strong className="text-[var(--text-primary)]">3.6% off</strong><br/>
                                    3+ vials → <strong className="text-[var(--text-primary)]">5.3% off</strong><br/>
                                    Applies per line item.
                                </p>
                            </div>
                        </div>
                    </aside>

                    {/* Grid */}
                    <div className="lg:col-span-9">
                        <div className="flex items-center justify-between mb-6">
                            <div className="data-label">{loading ? "Loading…" : `${items.length} compounds`}</div>
                            {(q || category) && (
                                <button onClick={() => setParams({})} data-testid="clear-filters" className="data-label text-brand hover:text-primary">
                                    Clear Filters →
                                </button>
                            )}
                        </div>
                        {loading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="card aspect-[3/4] animate-pulse" />
                                ))}
                            </div>
                        ) : items.length === 0 ? (
                            <div className="card p-12 text-center">
                                <p className="text-secondary">No compounds match your filters.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
